import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api/api-backend";

const STALE_TIME = 1000 * 20;
const MAX_RETRIES = 3;

/**
 * Retry on transient/server-side failures (5xx, network drops, gateway timeouts
 * from the load balancer or DB), but never on 4xx — a 400/401/404/409/422 won't
 * succeed on retry. Applies to queries and mutations alike: a cart key the
 * backend no longer knows about used to be fetched four times over seven
 * seconds before the store could clear it. Per-hook `retry` config still
 * overrides this default.
 */
const retryOnTransientError = (failureCount: number, error: unknown) => {
  if (failureCount >= MAX_RETRIES) return false;
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return true;
};

const retryDelay = (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      refetchOnWindowFocus: false,
      retry: retryOnTransientError,
      retryDelay,
    },
    mutations: {
      retry: retryOnTransientError,
      retryDelay,
    },
  },
});
